import { languageService } from "@/services/language/language.service.ts";
import { create } from "zustand";
import { produce } from "immer";
import { useEffect, useRef, useState } from "react";
import isNil from "lodash.isnil";

/**
 * The interface for the general store
 */
export interface GeneralState {
    /** True if there was an error during app initialization */
    appInitializationError: boolean;
    /** The current locale */
    currentLocale: string;
    /** Map of async operation ids and their loading state */
    openAsyncOperations: Record<string, number>;
    /** True if the global app bar shall be shown */
    showGlobalBar: boolean;
}

/**
 * Actions used by the general store
 */
export interface GeneralActions {
    /** Sets the current app locale */
    setCurrentLocale: (languageKey: string) => void;
    /**
     * Registers an async or long-lasting operation
     * @param id The id of the operation (used for tracking)
     */
    openAsyncOperation: (id: string) => void;
    /**
     * Closes an async or long-lasting operation
     * @param id The id of the operation
     * @param force True if closing of the operation shall be forced
     */
    closeAsyncOperation: (id: string, force?: boolean) => void;
    /** Sets {@link GeneralState#showGlobalBar} to true */
    setShowGlobalBar: (show?: boolean) => void;
}

/**
 * Validates the given operation id
 */
const validateOperationId = (id: string) => {
    if (isNil(id) || id.trim().length === 0) {
        throw new Error("ID for async operations cannot be empty!");
    }
};

/**
 * The general store
 */
export const useGeneralStore = create<GeneralState & GeneralActions>()((set) => ({
    appInitializationError: false,
    currentLocale: languageService.getCurrentLocale(),
    openAsyncOperations: {},
    showGlobalBar: true,
    setCurrentLocale: (languageKey) => {
        languageService.setLanguage(languageKey);
        set((store) => ({ ...store, currentLocale: languageKey }));
    },
    openAsyncOperation: (id) => {
        validateOperationId(id);
        set(
            produce((state: GeneralState) => {
                const count = state.openAsyncOperations[id];
                if (isNil(count)) state.openAsyncOperations[id] = 1;
                else state.openAsyncOperations[id] = count + 1;
            }),
        );
    },
    closeAsyncOperation: (id, force) => {
        validateOperationId(id);
        set(
            produce((state: GeneralState) => {
                const count = state.openAsyncOperations[id];
                if (force || isNil(count) || count < 2) delete state.openAsyncOperations[id];
                else state.openAsyncOperations[id]!--;
            }),
        );
    },
    setShowGlobalBar: (show = true) => {
        set((store) => ({ ...store, showGlobalBar: show }));
    },
}));

/**
 * Custom hook for indicating whether the app is currently loading something.
 * It immediately sets loading to true when loading occurs but ensures it stays
 * true for at least 300ms before setting back to false after loading ends.
 */
export const useIsLoading = (expectInitialLoad = false, ...ids: string[]): boolean => {
    const [internalIsLoading, setInternalIsLoading] = useState(expectInitialLoad);
    const openAsyncOperations = useGeneralStore((state) => state.openAsyncOperations);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Determine whether the given operations are currently being executed
    let isLoading = false;
    if (ids.length === 0) {
        isLoading = Object.keys(openAsyncOperations).length > 0;
    } else {
        for (const id of ids) {
            if (!isNil(openAsyncOperations[id])) {
                isLoading = true;
                break;
            }
        }
    }

    useEffect(() => {
        if (isLoading) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            // Set internalIsLoading to true immediately
            setInternalIsLoading(true);
        } else {
            // Set a timeout to set internalIsLoading to false after 300ms
            timeoutRef.current = setTimeout(() => {
                setInternalIsLoading(false);
                timeoutRef.current = null;
            }, 400);
        }

        // Cleanup function to clear the timeout if the component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isLoading]);

    return internalIsLoading;
};
